import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
}

export function useConfirm() {
  const { t } = useTranslation('common');
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | undefined>(undefined);

  const confirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleResult = (value: boolean) => {
    resolveRef.current?.(value);
    setOptions(null);
  };

  const ConfirmDialog = () =>
    options ? (
      <Dialog open onOpenChange={(open) => !open && handleResult(false)} title={options.title} description={options.description}>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => handleResult(false)}>
            {options.cancelLabel ?? t('actions.cancel')}
          </Button>
          <Button variant={options.variant === 'danger' ? 'danger' : 'primary'} onClick={() => handleResult(true)}>
            {options.confirmLabel ?? t('actions.confirm')}
          </Button>
        </div>
      </Dialog>
    ) : null;

  return { confirm, ConfirmDialog };
}
